/**
 * The two block types of the portal guide, as the client reads them.
 *
 * The server attaches at most one `navigate` and one `highlight` block to an
 * answer from the `general/portal-guide` tool (CONTRACT.md §7.3). They travel
 * along as resources in the existing `payload` channel — no new stream, no
 * tri-model release: `Message.resources` is open.
 *
 * ```json
 * { "type": "navigate", "name": "API-Schlüssel öffnen", "data": {
 *     "featureId": "settings.agent.tab.apiKeys", "route": "/settings?panel=agent&tab=apiKeys",
 *     "label": { "de": "API-Schlüssel öffnen", "en": "Open API keys" },
 *     "guideId": "settings.apiKeys.create", "auto": false,
 *     "prefill": { "guide": "settings.alerts.create", "name": "Batterie Groß-Gerau" } } }
 * { "type": "highlight", "name": "Schlüssel anlegen", "data": {
 *     "guideId": "settings.apiKeys.create", "label": { "de": "Schlüssel anlegen", "en": "Create key" },
 *     "hint": { "de": "oben über der Tabelle", "en": "above the table" } } }
 * ```
 *
 * This file is the **only** place that recognizes such a block. Both
 * consumers — main chat (`ChatExchange`/`ChatMessage`) and question panel
 * (`overlayBotStream`/`OverlayBotChat`) — ask here; two separate checks would
 * drift apart, and the weaker of the two would be the one that counts.
 *
 * **The blocks are data, not instructions.** They originate from model and
 * tool output. `navigateHrefFrom` therefore only hands out an address that
 * passes the same check as a clicked link in the answer text
 * (`isNavigableGuideHref`) — the same defense against `/\evil.example/settings`,
 * against unfilled placeholders (`{selectedEntity}`) and against foreign
 * schemes, and the same domain boundary (`/entity` without `selectedEntityType`).
 * And `route` is the *whole* address: nothing is assembled from `featureId`,
 * `prefill` or `params` that is not already in it (the server appends the
 * prefill, §7.5).
 *
 * **Text mirroring is mandatory** (§7.3): `answerMarkdown` still carries the
 * route and the markdown link. A client that does not know this file therefore
 * loses nothing — it just shows no button.
 */

import { GUIDE_ID_PATTERN } from './guideIds';
import type { Localized } from './types';
import { isNavigableGuideHref } from './useGuideNavigate';

/** The registry's languages — the same keys as in `Localized`. */
export type GuideLocale = 'de' | 'en';

/** The block types a guide client knows. */
// Stage 3 (UX_CHAT/PLAN.md AP7/AP8): `question` is the follow-up question with buttons and
// candidates, `confirm` the confirmation card. Both are rendered inline in the message.
export const GUIDE_BLOCK_TYPES = ['navigate', 'highlight', 'question', 'confirm'] as const;
export type GuideBlockType = (typeof GUIDE_BLOCK_TYPES)[number];

/**
 * Payload of a `navigate` block. `route` is required (without an address there
 * is nothing to open); the server contributes everything else as far as it has it.
 */
export interface NavigateBlockData {
    /** Registry id of the hit — evidence only, the client does not look anything up with it. */
    featureId?: string;
    /** Canonical address from the registry, placeholders filled, possibly with `&guide=…`. */
    route: string;
    label?: Localized;
    /** Target to point at after arrival; belongs to the `highlight` block, here evidence only. */
    guideId?: string;
    /** Only `true` when the user gave a command („öffne …") — never for a question. */
    auto?: boolean;
    prefill?: Record<string, string>;
}

/**
 * Fields the client attaches to a resource so `ChatMessage` can match it to its
 * placeholder in the text (`data-resource-id`). They do not come from the
 * server; `_id` does, once the conversation has been saved.
 */
interface ClientResourceIdentity {
    reference?: string;
    _id?: string;
    _stableId?: string;
    _streamingContext?: boolean;
}

export interface NavigateBlock extends ClientResourceIdentity {
    type: 'navigate';
    /** The tool's short name; the button prefers `data.label`. */
    name?: string;
    data: NavigateBlockData;
}

export interface HighlightBlockData {
    /** `data-guide-id` of the target (`guideIds.ts`, registry field `guideId`). */
    guideId: string;
    label?: Localized;
    /** Short location hint („oben über der Tabelle"). */
    hint?: Localized;
}

export interface HighlightBlock extends ClientResourceIdentity {
    type: 'highlight';
    name?: string;
    data: HighlightBlockData;
}

export type QuestionOptionId = 'yes' | 'no' | 'create' | 'choose' | 'cancel';

/**
 * The follow-up question (stage 3). The server computed it — text, candidates, options. The portal
 * shows buttons; every click becomes a message with `planId` and answer, free text remains
 * possible. The candidates deliberately carry only `n` and `label`: the ids stay on the server.
 */
export interface QuestionBlockData {
    planId: string;
    questionKey: string;
    kind: 'missingRelation' | 'ambiguousRelation' | 'incompleteFields' | 'offer' | 'promptTool';
    /**
     * `card`: this question renders as a card even while question cards are off — the server asks
     * for it (the prompt-tool plan, whose answers are choices, not an object search).
     */
    presentation?: 'card';
    relation?: string;
    targetClass?: string;
    round: number;
    text: Localized;
    candidates?: Array<{ n: number; label: string }>;
    /** The inline form for `incompleteFields`: one input per field, typed by the server from tri-model. */
    fields?: Array<{ name: string; label: Localized; type?: QuestionFieldInput; required?: boolean; unit?: string }>;
    options: Array<{ id: QuestionOptionId; label: Localized }>;
}
export type QuestionFieldInput = 'string' | 'text' | 'number' | 'date' | 'boolean';
export interface QuestionBlock extends ClientResourceIdentity {
    type: 'question';
    name?: string;
    data: QuestionBlockData;
}

/** The confirmation card (stage 3). `[Ja]` goes to the approval route, never to the model. */
export interface ConfirmBlockRow {
    path: string;
    className: string;
    classLabel: Localized;
    state: 'new' | 'existing';
    label: string;
    fields: Array<{ name: string; label: Localized; value: string }>;
    references: Array<{ name: string; label: Localized; target: string; state: 'existing' | 'planned' }>;
}
export interface ConfirmBlockData {
    planId: string;
    title: Localized;
    rows: ConfirmBlockRow[];
    summary: Localized;
    offers: Array<{ questionKey: string; text: Localized }>;
    expiresAt: string;
    /** `confirmPublish`: create and publish internally (prompt-tool plans) — a decision like `confirm`. */
    actions: Array<{ id: 'confirm' | 'confirmPublish' | 'edit' | 'cancel'; label: Localized }>;
}
export interface ConfirmBlock extends ClientResourceIdentity {
    type: 'confirm';
    name?: string;
    data: ConfirmBlockData;
}

export type GuideBlock = NavigateBlock | HighlightBlock | QuestionBlock | ConfirmBlock;

/** The language as a registry key; everything except English is German. */
export function guideLocale(language?: string | null): GuideLocale {
    return language && language.toLowerCase().startsWith('en') ? 'en' : 'de';
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

/**
 * The same limit as on the server side (`portalGuideBlocks.ts`,
 * `MAX_LABEL_CHARS`). The server already truncates, but the blocks reach the
 * client through the ```payload``` fence in the answer stream, which the model
 * itself can also write — a 100 kB label would yield a screen-filling button
 * and an endless screen reader announcement.
 */
export const MAX_GUIDE_LABEL_CHARS = 200;

function nonEmptyString(value: unknown): string | undefined {
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    if (trimmed.length === 0) return undefined;
    return trimmed.length > MAX_GUIDE_LABEL_CHARS ? trimmed.slice(0, MAX_GUIDE_LABEL_CHARS) : trimmed;
}

/**
 * A `navigate` block — checked is what the client needs: the type and an
 * address. A missing label does **not** invalidate the block; it only costs the
 * button's caption (the renderer then uses `guide.navigate.open`). Whether the
 * address is usable is decided by `navigateHrefFrom` — the split is
 * intentional: the filter in the resource sorter wants to know *whether* a
 * block of this type is present, even if its address is discarded.
 */
export function isNavigateBlock(resource: unknown): resource is NavigateBlock {
    if (!isRecord(resource) || resource.type !== 'navigate') return false;
    return isRecord(resource.data) && typeof resource.data.route === 'string';
}

/** A `highlight` block: type plus a target that can be pointed at. */
export function isHighlightBlock(resource: unknown): resource is HighlightBlock {
    if (!isRecord(resource) || resource.type !== 'highlight') return false;
    return isRecord(resource.data) && typeof resource.data.guideId === 'string';
}

/**
 * One of the guide's block types — the check by which consumers decide whether
 * a resource belongs to the guide (pattern: `isSourceResource`).
 */
export function isQuestionBlock(resource: unknown): resource is QuestionBlock {
    if (!isRecord(resource) || resource.type !== 'question') return false;
    const data = resource.data;
    return (
        isRecord(data) &&
        typeof data.planId === 'string' &&
        typeof data.questionKey === 'string' &&
        isRecord(data.text) &&
        Array.isArray(data.options)
    );
}
export function isConfirmBlock(resource: unknown): resource is ConfirmBlock {
    if (!isRecord(resource) || resource.type !== 'confirm') return false;
    const data = resource.data;
    return isRecord(data) && typeof data.planId === 'string' && Array.isArray(data.rows) && Array.isArray(data.actions);
}
export function isGuideBlock(resource: unknown): resource is GuideBlock {
    return isNavigateBlock(resource) || isHighlightBlock(resource) || isQuestionBlock(resource) || isConfirmBlock(resource);
}

/**
 * Text in the user's language. `de` is the fallback, not `en`: the registry
 * keeps German as the source, the translation may be missing. A plain string is
 * accepted because older tool versions and the concept sketch (§6.4 v2) sent
 * `label` unlocalized — a button with a German caption is better than none.
 */
export function localizedGuideText(value: unknown, locale: GuideLocale): string | undefined {
    const direct = nonEmptyString(value);
    if (direct) return direct;
    if (!isRecord(value)) return undefined;
    return nonEmptyString(value[locale]) ?? nonEmptyString(value.de) ?? nonEmptyString(value.en);
}

/** The button's caption, if the block brings one (`data.label`, otherwise `name`). */
export function navigateBlockLabel(
    block: NavigateBlock,
    locale: GuideLocale,
): string | undefined {
    return localizedGuideText(block.data.label, locale) ?? nonEmptyString(block.name);
}

/**
 * The block's address — or `null`.
 *
 * **Only** `route` is taken, and only if it passes the same check as a clicked
 * link in the answer text (`isNavigableGuideHref`): portal-internal, one
 * leading slash, no `//host`, no backslash, no unfilled placeholder — and no
 * address the navigator would reject anyway (`/entity` without
 * `selectedEntityType`). Whatever it rejects gets no button and no automatic
 * navigation — the route is in the answer text.
 *
 * `route` is deliberately read raw (only trimmed), not via `nonEmptyString`:
 * its 200-character truncation is meant for labels. A truncated address would
 * be a **different** address — in the worst case a valid one that leads
 * somewhere else.
 */
export function navigateHrefFrom(block: unknown): string | null {
    if (!isNavigateBlock(block)) return null;
    const route = typeof block.data.route === 'string' ? block.data.route.trim() : '';
    if (route === '') return null;
    return isNavigableGuideHref(route) ? route : null;
}

/**
 * Should the client jump on its own? Only on an explicit `auto: true` — the
 * server sets it only for a command („öffne …"), never for a question
 * („wo finde ich …", §7.3).
 */
export function isAutoNavigate(block: unknown): boolean {
    return isNavigateBlock(block) && block.data.auto === true;
}

/**
 * The `highlight` block as a request for the spotlight store (`spotlight.ts`)
 * — already localized, because the store knows no language.
 *
 * The id is checked against `GUIDE_ID_PATTERN` even though `GuideSpotlight`
 * does so again: a value from tool output goes into a `querySelector`
 * expression, and the check belongs at the boundary, not only at the consumer.
 */
export function spotlightFromHighlightBlock(
    block: unknown,
    locale: GuideLocale,
): { guideId: string; label?: string; hint?: string } | null {
    if (!isHighlightBlock(block)) return null;
    const guideId = nonEmptyString(block.data.guideId);
    if (!guideId || !GUIDE_ID_PATTERN.test(guideId)) return null;
    return {
        guideId,
        label: localizedGuideText(block.data.label, locale) ?? nonEmptyString(block.name),
        hint: localizedGuideText(block.data.hint, locale),
    };
}
